import React, { CSSProperties, useRef } from 'react';
import { fromThemeColors, IconProps, ThemeColor } from '@sharefiledev/flow-web';
import {
	colourCount,
	ComponentTypes,
	DEFAULT_GAP_CHECKBOXES,
	defaultComponentDimensions,
	defaultWidth,
	Field,
	getAllFields,
	IRecipient,
	MERGE_FIELD_STATES,
	SIGNER_COLORS,
} from '../../../common/componentUtils';
import { SchemaProperty } from '../../../data/interfaces/SchemaResponse';
import {
	StyledGhostComponent,
	StyledIconLabel,
	StyledLabel,
} from './GhostComponent.styled';

export interface GhostComponentProps {
	selectedComponentType: ComponentTypes;
	selectedSigner: IRecipient | null | undefined;
	pageBoundingClientRect: { width: number; height: number };
	ghostPosition: any;
	selectedProperty?: SchemaProperty;
}

export function GhostComponent({
	selectedComponentType,
	selectedSigner,
	pageBoundingClientRect,
	ghostPosition,
	selectedProperty,
}: GhostComponentProps) {
	const ghostComponentRef = useRef<HTMLDivElement>(null);

	const scaleValue = pageBoundingClientRect.width / defaultWidth;
	const scale = scaleValue > 1 ? 1 : scaleValue;

	const fields: Field[] = getAllFields();
	const field = fields.find(f => f.type === selectedComponentType);
	const Icon: React.FC<IconProps> = field?.icon!;

	const isMergeField = selectedProperty;
	const label = isMergeField
		? selectedProperty
			? selectedProperty.name
			: ''
		: fields.find(f => f.type === selectedComponentType)?.label;
	const signerSequence = selectedSigner?.sequence ?? -1;
	const signerColor = selectedProperty
		? MERGE_FIELD_STATES.unfilled
		: SIGNER_COLORS[signerSequence % (colourCount - 1)];
	const isAnnotation = selectedSigner === null;

	const isCheckboxGroup = selectedComponentType === ComponentTypes.CheckGroupComponent;

	const style = {
		width: ghostPosition.width,
		height: ghostPosition.height,
		left: ghostPosition.mouseX + 'px',
		top: ghostPosition.mouseY + 'px',
		display: 'flex',
	} as CSSProperties;

	const { labelThresholdWidth } = defaultComponentDimensions[selectedComponentType];
	const ghostComponentWidth = Number((style.width as string).replace('px', ''));
	const ghostComponentHeight = Number((style.height as string).replace('px', ''));

	return (
		<>
			<StyledGhostComponent
				isCheckboxGroup={isCheckboxGroup}
				isAnnotation={isAnnotation}
				signerColor={signerColor?.name}
				ref={ghostComponentRef}
				style={style}
				data-testid="ghost-component"
			>
				{((isAnnotation &&
					selectedComponentType === ComponentTypes.SignatureAnnotationComponent) ||
					!isAnnotation) && (
					<StyledIconLabel
						style={{
							transform: `scale(${scale})`,
						}}
					>
						<Icon
							size={16}
							color={fromThemeColors(ThemeColor.primaryText)({ theme: {} })}
						/>
						{!isCheckboxGroup && labelThresholdWidth < ghostComponentWidth && label ? (
							<StyledLabel>{label}</StyledLabel>
						) : null}
					</StyledIconLabel>
				)}
			</StyledGhostComponent>
			{isCheckboxGroup && (
				<StyledGhostComponent
					isCheckboxGroup={isCheckboxGroup}
					isAnnotation={isAnnotation}
					signerColor={signerColor?.name}
					ref={ghostComponentRef}
					style={{
						...style,
						top:
							ghostPosition.mouseY + ghostComponentHeight + DEFAULT_GAP_CHECKBOXES + 'px',
					}}
					data-testid="ghost-component"
				>
					<StyledIconLabel
						style={{
							transform: `scale(${scale})`,
						}}
					>
						<Icon
							size={16}
							color={fromThemeColors(ThemeColor.primaryText)({ theme: {} })}
						/>
						{!isCheckboxGroup && labelThresholdWidth < ghostComponentWidth && label ? (
							<StyledLabel>{label}</StyledLabel>
						) : null}
					</StyledIconLabel>
				</StyledGhostComponent>
			)}
		</>
	);
}
